import { useSessionTypesEditor } from "components/Dashboard/DoctorDashboard/views/one-one-sessions/contexts/SessionTypesEditorContext";
import { useSessionTypeEditor } from "components/Dashboard/DoctorDashboard/views/one-one-sessions/contexts/session-type-editor-context/SessionTypeEditorContext";
import ConfirmModal from "components/confirm-modal";
import ModalLayout from "components/modal/modal-layout/ModalLayout";
import SessionTypesUI from "components/session-types-ui/SessionTypesUI";
import { useSessionTypes } from "contexts/SessionTypesContext";
import useToggleModal from "hooks/useToggleModal";
import { isEqual } from "lodash";
import messages from "messages";
import { useCallback, useEffect, useMemo, useState } from "react";
import { Button } from "react-bootstrap";
import postSessionTypes from "services/scheduler/postAppointmentTypes";
import { AmplitudePageName, track } from "utils/amplitude";
import styles from "./SessionTypesEditor.module.scss";
import batchPutPatientProfile from "services/patient-chart/batchPutPatientProfile";

const ANALYTICS_COMPONENT_NAME = "edit-1-to-1-session-types-modal";

interface Props {
  analyticsPageName: AmplitudePageName;
}

/**
 * renders session types editor (modal)
 */
const SessionTypesEditor: React.FC<Props> = ({ analyticsPageName }) => {
  const {
    showSessionTypesEditor,
    openSessionTypesEditor,
    closeSessionTypesEditor,
  } = useSessionTypesEditor();
  const { showSessionTypeEditor, openSessionTypeEditor } =
    useSessionTypeEditor();
  const { sessionTypes: initialSessionTypes, setSessionTypes: onDone } =
    useSessionTypes();
  const [sessionTypes, setSessionTypes] =
    useState<SessionType[]>(initialSessionTypes);

  // track parent session types
  useEffect(() => {
    setSessionTypes(initialSessionTypes);
  }, [initialSessionTypes]);

  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<any>();

  const isDirty = useMemo(
    () => !isEqual(initialSessionTypes, sessionTypes),
    [initialSessionTypes, sessionTypes],
  );

  const handleHide = () => {
    setSessionTypes(initialSessionTypes);
    setError(undefined);
    setBusy(false);
    closeSessionTypesEditor();
  };

  // when changed, clear error
  useEffect(() => {
    setError(undefined);
  }, [sessionTypes]);

  const handleApply = async () => {
    let changedSessionTypes: SessionType[] = [];

    // todo: this does not include the case where a session type is deleted and added back/modified

    if (initialSessionTypes.length === sessionTypes.length) {
      changedSessionTypes = sessionTypes.filter((sessionType, index) => {
        const originalSessionType = initialSessionTypes[index];
        return !isEqual(sessionType, originalSessionType);
      });
    }

    setBusy(true);

    try {
      await postSessionTypes(sessionTypes);

      for (const sessionType of changedSessionTypes) {
        const index = sessionTypes.findIndex((st) => st === sessionType);
        const originalSessionType = initialSessionTypes[index];

        if (originalSessionType) {
          await batchPutPatientProfile(
            originalSessionType.label,
            sessionType.label,
          );
        }
      }

      if (onDone) {
        onDone(sessionTypes);
      }
      handleHide();
    } catch (error) {
      setError(error);
    } finally {
      setBusy(false);
    }
  };

  // delete confirm
  const {
    show: showConfirmDeletion,
    open: openConfirmDeletion,
    close: closeConfirmDeletion,
  } = useToggleModal(false);
  const [deleteContent, setDeleteContent] = useState<SessionType | undefined>();

  const handleOpenConfirm = useCallback(
    (arg: SessionType) => {
      setDeleteContent(arg);
      openConfirmDeletion();
      closeSessionTypesEditor();
    },
    [closeSessionTypesEditor, openConfirmDeletion],
  );

  const handleCloseConfirm = useCallback(() => {
    closeConfirmDeletion();
    openSessionTypesEditor();
  }, [closeConfirmDeletion, openSessionTypesEditor]);

  const sessionTypeEditorProps = useMemo(
    () => ({
      // check label duplicated
      getIsLabelInvalid: (arg: string | undefined) =>
        !arg ? false : sessionTypes.some((el) => el.label === arg),
    }),
    [sessionTypes],
  );

  return (
    <>
      <ModalLayout
        show={showSessionTypeEditor ? false : showSessionTypesEditor}
        error={error}
        onHide={handleHide}
        Header={messages.mySessionTypes}
        showHeaderDivider={true}
        Footer={
          <>
            <Button
              variant={"outline-primary"}
              className={styles["session-types-editor__action"]}
              onClick={() => {
                track(
                  analyticsPageName,
                  [ANALYTICS_COMPONENT_NAME, "cancel-button"],
                  "click",
                );
                handleHide();
              }}
              disabled={busy}
            >
              {messages.cancel}
            </Button>
            <Button
              className={styles["session-types-editor__action"]}
              disabled={busy || !isDirty}
              onClick={() => {
                track(
                  analyticsPageName,
                  [ANALYTICS_COMPONENT_NAME, "save-button"],
                  "click",
                );

                handleApply();
              }}
            >
              {messages.save}
            </Button>
          </>
        }
        disabled={busy}
        className={styles["session-types-editor"]}
        headerClass={styles["session-types-editor__header"]}
        bodyClass={styles["session-types-editor__body"]}
        footerClass={styles["session-types-editor__footer"]}
      >
        <SessionTypesUI
          sessionTypes={sessionTypes}
          onApply={(v, i) =>
            setSessionTypes([
              ...sessionTypes.slice(0, i),
              v,
              ...sessionTypes.slice(i + 1),
            ])
          }
          onEdit={(v, i) => {
            track(
              analyticsPageName,
              [ANALYTICS_COMPONENT_NAME, "edit-button"],
              "click",
            );

            openSessionTypeEditor(
              {
                ...sessionTypeEditorProps,
                onApply: (nextV: SessionType) =>
                  setSessionTypes((s) => [
                    ...s.slice(0, i),
                    nextV,
                    ...s.slice(i + 1),
                  ]),
              },
              analyticsPageName,
              v,
            );
          }}
          onDelete={(arg: SessionType) => {
            track(
              analyticsPageName,
              [ANALYTICS_COMPONENT_NAME, "delete-button"],
              "click",
            );

            handleOpenConfirm(arg);
          }}
          onAdd={() => {
            openSessionTypeEditor(
              {
                ...sessionTypeEditorProps,
                onApply: (nextV: SessionType) =>
                  setSessionTypes((s) => [...s, nextV]),
              },
              analyticsPageName,
            );
          }}
          setSessionTypes={setSessionTypes}
          variant="default"
        />
      </ModalLayout>

      <ConfirmModal
        labelTitle={messages.deleteSessionType}
        children={messages.theSessionInformationWillNotBeSaved}
        labelConfirm={messages.delete}
        show={showConfirmDeletion}
        onCancel={() => {
          track(
            analyticsPageName,
            [ANALYTICS_COMPONENT_NAME, "confirm-modal-cancel"],
            "click",
          );
          handleCloseConfirm();
        }}
        onConfirm={() => {
          track(
            analyticsPageName,
            [ANALYTICS_COMPONENT_NAME, "confirm-modal-confirm"],
            "click",
          );
          if (deleteContent) {
            const itemId = sessionTypes.findIndex(
              (el) => el?.label === deleteContent.label,
            );

            setSessionTypes([
              ...sessionTypes.slice(0, itemId),
              ...sessionTypes.slice(itemId + 1),
            ]);
          }
          setDeleteContent(undefined);
          handleCloseConfirm();
        }}
      />
    </>
  );
};
export default SessionTypesEditor;
